/* eslint-disable no-sequences */
import classNames from 'classnames';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import React, { FC, useCallback, useMemo } from 'react';
import { Order, useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import { OrganizationMemberType } from '.';
import { DashboardSettingsMembersActions } from './actions';

export type DashboardSettingsMembersListHeaderTypes = 'name' | 'email';

export type DashboardSettingsMembersListHeaderSortType =
  TableSortableColumnHeaderSortType<DashboardSettingsMembersListHeaderTypes>;

function getSortedMembers(
  members: OrganizationMemberType[],
  sort_by: string,
  sort_order: Order
): OrganizationMemberType[] {
  if (!sort_by) {
    return members;
  }
  let result = sortBy(members, [
    (v: OrganizationMemberType) => (sort_by === 'name' ? v.name : sort_by === 'email' ? v.email : null),
  ]);
  if (sort_order === 'desc') {
    result = reverse(result);
  }

  return result;
}

export interface DashboardSettingsMembersListProps {
  currentUserId: OrganizationUser['id'];
  members: OrganizationMemberType[];
}

export const DashboardSettingsMembersList: FC<DashboardSettingsMembersListProps> = ({ currentUserId, members }) => {
  const {
    filterBarParams: { sort_by, sort_order },
    updateSort,
  } = useFilterBarParams();

  const onTableHeaderClick = useCallback((sortStack: DashboardSettingsMembersListHeaderSortType) => {
    updateSort(sortStack.sortName, sortStack.orderBy);
  }, []);

  const sortedMembers = useMemo(() => getSortedMembers(members, sort_by, sort_order), [members, sort_by, sort_order]);

  const columns = useMemo(
    () => [
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='name'
            onClick={onTableHeaderClick}
            title='Name'
          />
        ),
        accessor: 'name',
        width: 190,
        Cell: ({ row }: { row: { original: OrganizationMemberType } }) => (
          <span
            className={classNames(
              'text-bedrock-p',
              row.original.isMember ? 'text-bedrock-black' : 'text-bedrock-dark-gray'
            )}
          >
            {row.original.name}
          </span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'email'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='email'
            onClick={onTableHeaderClick}
            title='Email'
          />
        ),
        accessor: 'email',
        width: 220,
        Cell: ({ row }: { row: { original: OrganizationMemberType } }) => (
          <span
            className={classNames(
              'text-bedrock-p',
              row.original.isMember ? 'text-bedrock-black' : 'text-bedrock-dark-gray'
            )}
          >
            {row.original.email}
          </span>
        ),
      }),
      generateColumn({
        Header: '',
        accessor: 'actions',
        width: '100%',
        Cell: ({ row }: { row: { original: OrganizationMemberType } }) => (
          <>
            {currentUserId !== row.original.id && (
              <div className='text-bedrock-p text-bedrock-black mr-2'>
                <div className='flex grow-0 justify-end'>
                  <DashboardSettingsMembersActions
                    isUserInvited={!row.original.isMember}
                    userId={`${row.original.id}`}
                  />
                </div>
              </div>
            )}
          </>
        ),
      }),
    ],
    [members, currentUserId, sort_by, sort_order, onTableHeaderClick]
  );

  return (
    <div className='flex w-full mt-4'>
      <DataTable
        tableClassName='w-full'
        columns={columns}
        data={sortedMembers}
        disableRowCursorPointer
        columnClasses={['break-words', 'break-words']}
      />
    </div>
  );
};
